let cached = null;
let inflight = null;

const CONTENT_URL = process.env.NODE_ENV === 'development'
  ? '/content.json'
  : '/.netlify/functions/content';

async function fetchAllContent() {
  if (cached) return cached;
  if (inflight) return inflight;

  inflight = (async () => {
    const res = await fetch(CONTENT_URL);
    if (!res.ok) throw new Error(`Content fetch failed: ${res.status}`);
    cached = await res.json();
    return cached;
  })();

  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

/**
 * Fetch About content (single record)
 */
export async function fetchAbout() {
  const { about } = await fetchAllContent();
  if (!about || about.length === 0) return null;
  return { content: about[0].fields.Content || '' };
}

/**
 * Fetch Press Quotes
 */
export async function fetchPressQuotes() {
  const { pressQuotes } = await fetchAllContent();
  return (pressQuotes || []).map(record => ({
    quote: record.fields.Quote || '',
    attribution: record.fields.Attribution || '',
  }));
}

/**
 * Fetch News items
 */
export async function fetchNews() {
  const { news } = await fetchAllContent();
  return (news || []).map(record => {
    const attachment = record.fields.Attachment?.[0];
    const imageUrl = attachment?.url || record.fields['Image URL'] || null;
    return {
      date: record.fields.Date || '',
      title: record.fields.Title || '',
      description: record.fields.Description || '',
      imageUrl,
    };
  });
}

/**
 * Fetch Tour Dates
 */
export async function fetchTourDates() {
  const { tourDates } = await fetchAllContent();
  return (tourDates || []).map(record => {
    const rawDate = record.fields['Show Date'] || '';
    let displayDate = rawDate;
    if (rawDate) {
      const d = new Date(rawDate + 'T00:00:00');
      displayDate = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase();
    }
    return {
      date: displayDate,
      venue: record.fields.Venue || '',
      location: record.fields.Location || '',
      ticketUrl: record.fields['Ticket URL'] || null,
    };
  });
}

/**
 * Fetch Media items
 */
export async function fetchMedia() {
  const { media } = await fetchAllContent();
  return (media || []).map((record, index) => {
    const attachment = record.fields.Attachment?.[0];
    const imageUrl = attachment?.url || record.fields['Image Path'] || '';
    return {
      id: record.id || `media-${index}`,
      title: record.fields.Title || '',
      image: imageUrl,
      credit: record.fields.Credit || '',
      type: record.fields.Type || 'image',
      url: record.fields['Video URL'] || '',
      category: record.fields.Category || '',
    };
  });
}

/**
 * Fetch Lyrics lines
 */
export async function fetchLyrics() {
  const { lyrics } = await fetchAllContent();
  return (lyrics || []).map(record => record.fields.Line ?? '');
}

/**
 * Fetch Shop Items
 */
export async function fetchShopItems() {
  const { shopItems } = await fetchAllContent();
  return (shopItems || []).map(record => {
    const productImage = record.fields['Product Image']?.[0];
    const imageUrl = productImage?.url || record.fields['Image URL'] || null;
    return {
      title: record.fields.Title || '',
      description: record.fields.Description || '',
      price: record.fields.Price || null,
      imageUrl,
      purchaseUrl: record.fields['Purchase URL'] || '',
      ctaText: record.fields['Text on the link'] || 'Visit Bandcamp to purchase →',
    };
  });
}
